import React, { useState, useEffect, useContext } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Person as PersonIcon, Email as EmailIcon, Badge as BadgeIcon, Edit as EditIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { GlobalContext } from "../../contexts/GlobalContext";
import UserAvatar, { invalidateAvatarCache } from "../../components/common/UserAvatar";
import ProfilePhotoCropDialog from "../../components/profile/ProfilePhotoCropDialog";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const InfoBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
}));

const AccountInfo = () => {
  const { globalData, fetchUserData } = useContext(GlobalContext);
  const [editMode, setEditMode] = useState(false);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);
  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    roles: [],
  });

  useEffect(() => {
    if (globalData) {
      const nameParts = globalData.displayName ? globalData.displayName.split(" ") : ["", ""];
      setUserInfo({
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: globalData.email || "",
        username: globalData.userName || "",
        roles: globalData.roles || [],
      });
    }
  }, [globalData]);

  const isLoading = !globalData || !globalData.email || !globalData.displayName;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    setEditMode(false);
    // Here you would typically make an API call to update the user information
  };

  const handleInputChange = (field, value) => {
    setUserInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRemoveProfilePhoto = async () => {
    setIsRemovingPhoto(true);
    try {
      if (globalData?.profilePhotoFileId) {
        invalidateAvatarCache(globalData.profilePhotoFileId);
      }
      await axios.delete("/api/v1/me/profile-photo");
      await fetchUserData();
    } catch (error) {
      console.error("Error removing profile photo:", error);
    } finally {
      setIsRemovingPhoto(false);
    }
  };

  const displayName = `${userInfo.firstName} ${userInfo.lastName}`.trim();

  return (
    <React.Fragment>
      <Helmet title="Account Information" />

      <Typography variant="h4" gutterBottom>
        Account Information
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        View and manage your account details and personal information.
      </Typography>

      <Grid container spacing={4}>
        {/* Profile Overview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={3} mb={3}>
                <UserAvatar
                  profilePhotoFileId={globalData?.profilePhotoFileId}
                  displayName={displayName}
                  size={80}
                  sx={{ fontSize: "2rem" }}
                />
                <Box>
                  <Typography variant="h5" gutterBottom>
                    {userInfo.firstName} {userInfo.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userInfo.email}
                  </Typography>
                  <Stack direction="row" spacing={1} mt={1}>
                    {userInfo.roles.map((role, index) => (
                      <Chip key={index} label={role} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" justifyContent="flex-end">
                {editMode ? (
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" onClick={() => setEditMode(false)}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave}>
                      Save Changes
                    </Button>
                  </Stack>
                ) : (
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditToggle}>
                    Edit Profile
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile photo */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profile photo
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Upload a photo for your account. It will appear in customer chat messages and in the navigation bar.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "center", sm: "flex-start" }} spacing={3}>
                <UserAvatar
                  profilePhotoFileId={globalData?.profilePhotoFileId}
                  displayName={displayName}
                  size={96}
                  sx={{ fontSize: "2.25rem" }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ pt: { sm: 1 } }}>
                  <Button variant="contained" onClick={() => setIsCropDialogOpen(true)}>
                    {globalData?.profilePhotoFileId ? "Change photo" : "Upload photo"}
                  </Button>
                  {globalData?.profilePhotoFileId && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveProfilePhoto}
                      disabled={isRemovingPhoto}
                    >
                      {isRemovingPhoto ? "Removing..." : "Remove"}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom mb={3}>
                Account Details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoBox>
                    <PersonIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle2" gutterBottom>
                        First Name
                      </Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={userInfo.firstName}
                          onChange={(e) => handleInputChange("firstName", e.target.value)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {userInfo.firstName || "Not specified"}
                        </Typography>
                      )}
                    </Box>
                  </InfoBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoBox>
                    <PersonIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Last Name
                      </Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={userInfo.lastName}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {userInfo.lastName || "Not specified"}
                        </Typography>
                      )}
                    </Box>
                  </InfoBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoBox>
                    <EmailIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Email Address
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {userInfo.email || "Not specified"}
                      </Typography>
                    </Box>
                  </InfoBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoBox>
                    <BadgeIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Username
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {userInfo.email || "Not specified"}
                      </Typography>
                    </Box>
                  </InfoBox>
                </Grid>

                <Grid item xs={12}>
                  <InfoBox>
                    <BadgeIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Roles & Permissions
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {userInfo.roles.length > 0 ? (
                          userInfo.roles.map((role, index) => <Chip key={index} label={role} size="small" color="primary" />)
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No roles assigned
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </InfoBox>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ProfilePhotoCropDialog
        open={isCropDialogOpen}
        onClose={() => setIsCropDialogOpen(false)}
        onSaved={() => setIsCropDialogOpen(false)}
      />
    </React.Fragment>
  );
};

export default AccountInfo;
